/**
 * 人物语义值归一化工具。
 *
 * 约定：
 * - 系统内部的 canonical 语义值统一使用中文。
 * - 仍兼容少量开发期残留的英文旧值，并在 parse/查询时自动折叠到中文。
 */

export const RACE_CANONICAL_MAP: Readonly<Record<string, string>> = Object.freeze({
  human: '人类',
  人类: '人类',
  elf: '精灵',
  精灵: '精灵',
  dwarf: '矮人',
  矮人: '矮人',
  beastkin: '兽人',
  兽人: '兽人',
  goblin: '哥布林',
  哥布林: '哥布林',
  dragonkin: '龙族',
  龙族: '龙族',
  demonkin: '魔族',
  魔族: '魔族',
  wolf: '狼',
  狼: '狼',
  bear: '熊',
  熊: '熊',
  slime: '史莱姆',
  史莱姆: '史莱姆',
  marsh_shambler: '沼泽爬行者',
  沼泽爬行者: '沼泽爬行者',
  undead: '亡灵',
  亡灵: '亡灵',
  spirit: '灵体',
  灵体: '灵体',
});

export const BUILD_TEMPLATE_CANONICAL_MAP: Readonly<Record<string, string>> = Object.freeze({
  noncombat: '非战斗',
  非战斗: '非战斗',
  civilian: '平民自保',
  平民自保: '平民自保',
  skirmisher: '游击突袭',
  游击突袭: '游击突袭',
  frontliner: '前线正面',
  前线正面: '前线正面',
  caster: '施法者',
  施法者: '施法者',
  support: '支援辅助',
  支援辅助: '支援辅助',
  elite_bruiser: '精英重压',
  精英重压: '精英重压',
  boss_controller: '首领控场',
  首领控场: '首领控场',
});

export const IDENTITY_TEMPLATE_CANONICAL_MAP: Readonly<Record<string, string>> = Object.freeze({
  tavern_owner: '酒馆店主',
  酒馆店主: '酒馆店主',
  innkeeper: '旅店店主',
  旅店店主: '旅店店主',
  shopkeeper: '店铺老板',
  店铺老板: '店铺老板',
  guild_receptionist: '公会接待',
  公会接待: '公会接待',
  temple_priest: '神殿司祭',
  神殿司祭: '神殿司祭',
  castle_steward: '城堡总管',
  城堡总管: '城堡总管',
});

export const ENCOUNTER_TIER_CANONICAL_MAP: Readonly<Record<string, string>> = Object.freeze({
  grunt: '杂兵',
  杂兵: '杂兵',
  elite: '精英',
  精英: '精英',
  boss: '首领',
  首领: '首领',
});

function normalizeCanonicalValue(
  value: string | undefined,
  canonicalMap: Readonly<Record<string, string>>,
): string {
  const normalized = value?.trim();
  if (!normalized) {
    return '';
  }

  return canonicalMap[normalized.toLowerCase()] || canonicalMap[normalized] || normalized;
}

export function normalizeRaceId(value: string | undefined): string {
  return normalizeCanonicalValue(value, RACE_CANONICAL_MAP);
}

export function normalizeOptionalRaceId(value: string | undefined): string | undefined {
  return normalizeRaceId(value) || undefined;
}

export function normalizeBuildTemplateId(value: string | undefined): string {
  return normalizeCanonicalValue(value, BUILD_TEMPLATE_CANONICAL_MAP);
}

export function normalizeOptionalBuildTemplateId(value: string | undefined): string | undefined {
  return normalizeBuildTemplateId(value) || undefined;
}

export function normalizeIdentityTemplateId(value: string | undefined): string {
  return normalizeCanonicalValue(value, IDENTITY_TEMPLATE_CANONICAL_MAP);
}

export function normalizeOptionalIdentityTemplateId(value: string | undefined): string | undefined {
  return normalizeIdentityTemplateId(value) || undefined;
}

export function normalizeEncounterTier(value: string | undefined): string {
  return normalizeCanonicalValue(value, ENCOUNTER_TIER_CANONICAL_MAP);
}

export function normalizeOptionalEncounterTier(value: string | undefined): string | undefined {
  return normalizeEncounterTier(value) || undefined;
}
